import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

type TrialNotificationVariant = 'initial' | 'fiveDay' | 'ended';

type TrialNotificationDialogProps = {
  open: boolean;
  variant: TrialNotificationVariant | null;
  daysRemaining?: number;
  onClose: () => void | Promise<void>;
  onNavigateBilling: () => void;
};

const helpEmbeds = [
  {
    key: 'board',
    label: 'How to use boards',
    src: 'https://demo.arcade.software/TsadkDAcXKqgTpkMMHqq?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    title: 'Create and Collaborate on Interactive Boards',
  },
  {
    key: 'whiteboard',
    label: 'How to use whiteboards',
    src: 'https://demo.arcade.software/uNO3qIViGDH0ZG6KN7Xm?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    title: 'Create and Share a Collaborative Whiteboard',
  },
  {
    key: 'quick-sort',
    label: 'How to use Quick Sorts',
    src: 'https://demo.arcade.software/Eo7etZ5kzysbn93zOawJ?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    title: 'How to use Quick Sorts',
  },
] satisfies Array<{
  key: string;
  label: string;
  src?: string;
  title?: string;
}>;

export const TrialNotificationDialog = ({
  open,
  variant,
  daysRemaining,
  onClose,
  onNavigateBilling,
}: TrialNotificationDialogProps) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedTab(0);
      setIsClosing(false);
      return;
    }

    setSelectedTab((currentTab) =>
      currentTab >= helpEmbeds.length ? 0 : currentTab
    );
  }, [open]);

  const handleTabChange = (_event: SyntheticEvent, value: number) => {
    setSelectedTab(value);
  };

  const title = useMemo(() => {
    if (variant === 'initial') {
      return 'Welcome to your free trial of dotstorming!';
    }

    if (variant === 'fiveDay') {
      return 'Just to remind you';
    }

    if (variant === 'ended') {
      return 'Trial ended';
    }

    return '';
  }, [variant]);

  const handleClose = async () => {
    if (isClosing || !variant) {
      return;
    }

    setIsClosing(true);
    try {
      await onClose();
    } finally {
      setIsClosing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => void handleClose()}
      fullWidth
      maxWidth={variant === 'initial' ? 'lg' : 'sm'}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers={variant === 'initial'}>
        {variant === 'initial' ? (
          <>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ mx: '-24px', mt: '-16px' }}
            >
              {helpEmbeds.map((embed) => (
                <Tab key={embed.key} label={embed.label} />
              ))}
            </Tabs>
            <Box sx={{ pt: 2, height: { xs: 360, md: 640 } }}>
              <iframe
                src={helpEmbeds[selectedTab]?.src ?? ''}
                title={helpEmbeds[selectedTab]?.title ?? ''}
                frameBorder="0"
                loading="lazy"
                allowFullScreen
                allow="clipboard-write"
                style={{
                  width: '100%',
                  height: '100%',
                  colorScheme: 'light',
                }}
              />
            </Box>
          </>
        ) : null}
        {variant === 'fiveDay' ? (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Just letting you know that you only have {daysRemaining} days left on your{' '}
              <Link component="a" underline="hover" onClick={onNavigateBilling}>
                Gold Account
              </Link>{' '}
              trial!
            </Typography>
            <Typography variant="body1">
              After it ends, your account will remain active but limited to one board.
            </Typography>
          </Box>
        ) : null}
        {variant === 'ended' ? (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your{' '}
              <Link component="a" underline="hover" onClick={onNavigateBilling}>
                Gold Account
              </Link>{' '}
              has ended.  You can still use your account, but it’s now limited to one board.
            </Typography>
            <Typography variant="body1">
              Upgrade to unlock unlimited boards, custom themes, and more powerful features.
            </Typography>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => void handleClose()} variant="contained" disabled={isClosing}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
